import {
  CircularProgress,
  Collapse,
  DarkTooltip,
  Divider,
  IconButton,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { DateUtil } from '@ltvco/refresh-lib/utils';
import {
  AccessTime,
  Close,
  DoneAll,
  KeyboardArrowDown,
  KeyboardArrowUp,
  NotificationsNone,
  AutoAwesome,
} from '@mui/icons-material';
import { useState, type KeyboardEvent } from 'react';

import {
  PanelRoot,
  PanelHeader,
  PanelBody,
  PanelFooter,
  DateGroupContainer,
  DateGroupHeader,
  SectionRow,
  FooterButton,
  MetadataIcon,
  ChangesList,
  ChangeItem,
} from 'components/DeepSearch/components/ReportLibrary/MonitoredReports/MonitoringChangesPanel/MonitoringChangesPanel.styles';
import {
  groupChangesByDate,
  groupChangesBySection,
  getSectionLabel,
} from 'components/DeepSearch/components/ReportLibrary/MonitoredReports/MonitoringChangesPanel/utils';

import type { ReportChange } from 'components/DeepSearch/components/ReportLibrary/MonitoredReports/MonitoringChangesPanel/types';

export type { ReportChange };

export interface MonitoringChangesPanelProps {
  reportTitle: string;
  reportType: string;
  lastUpdated?: string;
  changes: ReportChange[];
  onClose: () => void;
  onViewReport: () => void;
  onNewChat: () => void;
  isViewReportSupported?: boolean;
  viewReportDisabledTooltip?: string;
  hasUnviewedChanges?: boolean;
  onMarkViewed?: () => void;
  isMarkingViewed?: boolean;
}

const dateParser = new DateUtil();

export const MonitoringChangesPanel = ({
  reportTitle,
  reportType,
  lastUpdated,
  changes,
  onClose,
  onViewReport,
  onNewChat,
  isViewReportSupported = true,
  viewReportDisabledTooltip,
  hasUnviewedChanges = false,
  onMarkViewed,
  isMarkingViewed = false,
}: MonitoringChangesPanelProps) => {
  const totalUpdates = changes.length;

  const formattedLastUpdated = lastUpdated
    ? dateParser.parseDateFromString(lastUpdated, 'L/dd/yyyy')
    : undefined;

  const dateGroups = groupChangesByDate(changes);

  return (
    <PanelRoot>
      <PanelHeader spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Text variant="h3" fontWeight="bold" mt={0} mb={0}>
            Monitoring
          </Text>
          <IconButton
            onClick={onClose}
            aria-label="Close monitoring panel"
            size="small"
          >
            <Close />
          </IconButton>
        </Stack>

        <Stack spacing={1}>
          <Text variant="h4" fontWeight="bold" mt={0} mb={0}>
            {reportTitle}
          </Text>
          <Text variant="body2" color="text.secondary">
            {reportType}
          </Text>
        </Stack>

        <Stack spacing={1}>
          {formattedLastUpdated && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <MetadataIcon as={AccessTime} />
              <Text variant="caption" color="text.secondary">
                Last Updated: {formattedLastUpdated}
              </Text>
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <MetadataIcon as={NotificationsNone} />
              <Text variant="caption" color="text.secondary">
                Total Updates: {totalUpdates}
              </Text>
            </Stack>
            {onMarkViewed && (
              <DarkTooltip
                title={
                  hasUnviewedChanges ? 'Mark as viewed' : 'All updates viewed'
                }
                placement="top"
              >
                <span>
                  <IconButton
                    onClick={onMarkViewed}
                    aria-label="Mark all changes as viewed"
                    color="default"
                    disabled={!hasUnviewedChanges || isMarkingViewed}
                  >
                    {isMarkingViewed ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DoneAll />
                    )}
                  </IconButton>
                </span>
              </DarkTooltip>
            )}
          </Stack>
        </Stack>
      </PanelHeader>

      <PanelBody>
        {dateGroups.map(({ date, changes: dateChanges }) => (
          <DateGroup key={date} date={date} changes={dateChanges} />
        ))}
      </PanelBody>

      <PanelFooter>
        <FooterButton
          variant="outlined"
          fullWidth
          onClick={onViewReport}
          disabled={!isViewReportSupported}
          title={!isViewReportSupported ? viewReportDisabledTooltip : undefined}
        >
          View Report
        </FooterButton>
        <FooterButton
          variant="contained"
          fullWidth
          onClick={onNewChat}
          startIcon={<AutoAwesome />}
        >
          New Chat
        </FooterButton>
      </PanelFooter>
    </PanelRoot>
  );
};

interface DateGroupProps {
  date: string;
  changes: ReportChange[];
}

const DateGroup = ({ date, changes }: DateGroupProps) => {
  const [expanded, setExpanded] = useState(true);
  const sectionGroups = groupChangesBySection(changes);

  const formattedDate = dateParser.parseDateFromString(date, 'LLL dd, yyyy');

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <DateGroupContainer>
      <DateGroupHeader
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${formattedDate}, ${changes.length} updates`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <Text variant="subtitle2" fontWeight="bold">
          Date: {formattedDate}
        </Text>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Text variant="body2">
            {changes.length} update{changes.length !== 1 ? 's' : ''}
          </Text>
          {expanded ? (
            <KeyboardArrowUp fontSize="small" />
          ) : (
            <KeyboardArrowDown fontSize="small" />
          )}
        </Stack>
      </DateGroupHeader>

      <Collapse in={expanded}>
        {sectionGroups.map(({ section, changes: sectionChanges }, index) => (
          <SectionGroup
            key={section}
            section={section}
            changes={sectionChanges}
            showDivider={index > 0}
          />
        ))}
      </Collapse>
    </DateGroupContainer>
  );
};

interface SectionGroupProps {
  section: string;
  changes: ReportChange[];
  showDivider: boolean;
}

const SectionGroup = ({ section, changes, showDivider }: SectionGroupProps) => {
  const [expanded, setExpanded] = useState(false);
  const label = getSectionLabel(section);

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <>
      {showDivider && <Divider />}
      <SectionRow
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${label}, ${changes.length} items`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <Text variant="body2">{label}</Text>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Text variant="body2" fontWeight="bold">
            {changes.length}
          </Text>
          {expanded ? (
            <KeyboardArrowUp fontSize="small" />
          ) : (
            <KeyboardArrowDown fontSize="small" />
          )}
        </Stack>
      </SectionRow>

      <Collapse in={expanded}>
        <ChangesList component="ul">
          {changes.map((change) => (
            <ChangeItem component="li" key={change.id}>
              <Text variant="body2">{change.content || change.identifier}</Text>
            </ChangeItem>
          ))}
        </ChangesList>
      </Collapse>
    </>
  );
};
